<template>
  <app-layout>
    <template #header>
      <inertia-link
        :href="route('dashboard')"
        class="lg:absolute relative md:left-7 flex items-center text-vice-teal-500 text-xs"
      >
        <ChevronLeftIcon class="w-4 h-4 mr-1"></ChevronLeftIcon>
        <span class="flex-none">All Sessions</span>
      </inertia-link>
      <div
        class="
          pb-5
          pt-4
          2xl:pt-0
          border-b border-gray-200
          sm:flex
          sm:items-center
          sm:justify-between
        "
      >
        <div class="flex gap-x-5">
          <img
            :src="this.$page.props.session.details.image_url"
            class="w-auto h-32 img-fluid object-contain object-left"
            alt=""
          />
          <div>
            <div class="flex-col md:flex">
              <h2 class="text-3xl max-w-3xl text-vice-teal-900 font-semibold leading-tight">
                {{ this.$page.props.session.title }}
              </h2>
              <p class="pl-2 pt-3.5 truncate leading-9 text-xs text-dark-grey">
                with {{ this.$page.props.session.details.clinician?.name }}

                <span
                  v-show="this.$page.props.session.details.university?.name"
                  class="text-gray-900 font-medium"
                >
                  ({{ this.$page.props.session.details.university?.name }})
                </span>
              </p>
            </div>
            <div class="flex gap-5 pt-3">
              <p class="flex gap-1">
                <CalendarIcon class="h-5 w-5 text-gray-300"></CalendarIcon>
                <span class="text-gray-900 text-xs">
                  {{ yearFormatter(this.$page.props.session.details.session_start_by_timezone) }}
                </span>
              </p>
              <p class="flex gap-1">
                <ClockIcon class="h-5 w-5 text-gray-300"></ClockIcon>
                <span class="text-gray-900 text-xs">
                  {{ hoursFormatter(this.$page.props.session.details.session_start_by_timezone) }}
                </span>
              </p>
              <p class="flex gap-1" v-if="this.$page.props.session.details.year_of_study">
                <AcademicCapIcon class="h-5 w-5 text-gray-300"></AcademicCapIcon>
                <span class="text-gray-900 text-xs">
                  {{ this.$page.props.session.details.year_of_study }} year of study
                </span>
              </p>
              <p
                class="flex gap-1"
                v-if="this.$page.props.session.details.session_type === 'in_classroom'"
              >
                <LocationMarkerIcon class="h-5 w-5 text-gray-300"></LocationMarkerIcon>
                <span class="text-gray-900 text-xs">
                  {{ this.$page.props.session.details.room_location }}
                </span>
              </p>
            </div>
            <div class="pt-6">
              <p class="text-xs">{{ this.$page.props.session.summary }}</p>
            </div>
          </div>
        </div>
      </div>
      <div>
        <div class="sm:hidden py-2">
          <label for="tabs" class="sr-only">Select a tab</label>
          <select
            id="tabs"
            name="tabs"
            @change="setTabState($event.target.value)"
            class="
              block
              w-full
              focus:ring-indigo-500
              focus:border-indigo-500
              border-gray-300
              rounded-md
            "
          >
            <option
              v-for="tab in tabs"
              :key="tab.name"
              :value="tab.id"
              :selected="tab.current"
              :class="tab.visible ? 'block' : 'hidden'"
            >
              {{ tab.name }}
            </option>
          </select>
        </div>
        <div class="hidden sm:block">
          <div>
            <nav class="-mb-px flex" aria-label="Tabs">
              <button
                v-for="tab in tabs"
                :key="tab.name"
                @click="setTabState(tab.id)"
                :class="[
                  tab.visible ? 'block' : 'hidden',
                  tab.current
                    ? 'border-vice-teal-400 text-grey-900'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300',
                  'w-1/5 py-4 px-1 text-center border-b-2 font-medium text-xs',
                ]"
                :aria-current="tab.current ? 'page' : undefined"
              >
                {{ tab.name }}
              </button>
            </nav>
          </div>
        </div>
      </div>
    </template>
    <div class="max-w-10xl mx-auto pt-6 pb-10 px-2 sm:px-6">
      <!--  Overview tab -->
      <CourseRequiredAlert
        :show="!$page.props.userFinishedCourse"
        :url="$page.props.baseCourseUrl"
        class="rounded-md p-2 mt-5"
      />

      <div
        class="max-w-5xl mx-auto grid grid-cols-10 gap-8 pt-6 md:pt-11"
        :class="tabs[0].current ? 'block' : 'hidden'"
      >
        <div class="col-span-10 sm:col-span-6">
          <session-entry-card
            v-if="$page.props.showEnterSessionButtonForAttendee"
            @openSessionEntryWizard="openSessionEntryWizard"
          >
            <template v-slot:actions>
              <div class="flex w-full gap-4 justify-end">
                <button
                  v-if="
                    $page.props.userFinishedCourse &&
                    $page.props.session.details.session_type === 'in_classroom'
                  "
                  @click="participateOffline"
                  data-cy="offline-participation"
                  type="button"
                  v-tippy="
                    isObserver
                      ? {
                          content: 'You can ask questions at any time in the chat',
                          theme: 'light-border-11',
                          placement: 'top',
                        }
                      : null
                  "
                  class="
                    relative
                    inline-flex
                    items-center
                    justify-center
                    select-none
                    font-medium
                    pl-4
                    pr-3.5
                    py-2
                    h-11
                    text-sm text-gray-700
                    bg-white
                    border border-gray-300
                    focus:outline-none
                    focus:ring-gray-500
                    focus:border-gray-500
                    hover:bg-gray-50
                    sm:text-sm
                    rounded-md
                    content-center
                  "
                >
                  Access chat & reflection
                </button>
                <ViceButton
                  icon="LogoutIcon"
                  red
                  @click="openSessionEntryPrecheck"
                  v-if="$page.props.userFinishedCourse && !isMobile"
                  class="step-student-enter-session"
                  v-tippy="
                    isObserver
                      ? {
                          content:
                            'You cannot talk and your camera is off. You can ask questions at any time in the chat.',
                          theme: 'light-border-11',
                          placement: 'top',
                        }
                      : null
                  "
                >
                  Enter session
                </ViceButton>
                <ViceButton
                  icon="LogoutIcon"
                  red
                  v-if="!$page.props.userFinishedCourse"
                  :href="$page.props.baseCourseUrl"
                  target="_blank"
                >
                  Take course
                </ViceButton>
              </div>
            </template>
          </session-entry-card>

          <session-entry-wizard
            :open="isOpenSessionEntryWizard"
            @closeSessionEntryWizard="isOpenSessionEntryWizard = false"
          ></session-entry-wizard>

          <SessionEntryPrecheckWizard
            :precheckOpen="isOpenSessionEntryPrecheck"
            @closeSessionEntryPrecheckWizard="isOpenSessionEntryPrecheck = false"
            @openSessionEntryWizard="openSessionEntryWizard"
          />

          <ProofOfAttendance
            v-if="
              $page.props.presentMarker &&
              $page.props.session.finished_at &&
              !$page.props.hasPendingInvite
            "
          />
          <WhatYouLearn />
          <AboutSession />

          <SessionFiles v-if="$page.props.session.files.length" />
        </div>
        <div class="col-span-10 sm:col-span-4 flex flex-col gap-10">
          <invitation
            v-if="
              !$page.props.hasSession &&
              $page.props.hasPendingInvite &&
              $page.props.session.is_private
            "
          ></invitation>
          <!-- :showApplyButton="!(hasApplied || hasInvite || hasSession)" -->
          <application
            @userHasApplied="appliedUser"
            :applicationId="applicationId"
            :userSessionType="userSessionType"
          ></application>

          <about-clinician v-if="$page.props.session.details.clinician"></about-clinician>
        </div>
      </div>

      <!-- Recordings tab -->
      <div
        v-if="snapshotsExist && $page.props.isSnapshotsAndRecordingsEnabled"
        class="grid grid-cols-12 gap-4"
        :class="tabs[2].current ? 'block' : 'hidden'"
      >
        <action-message :on="added" message="Your comment has been added" />

        <!-- List column -->
        <div class="relative w-full mt-5 col-span-12 sm:col-span-2">
          <table class="min-w-full">
            <thead>
              <tr class="border-b border-gray-200">
                <th
                  scope="col"
                  class="
                    py-3
                    text-center text-xxs
                    font-medium
                    text-gray-500
                    tracking-wider
                    uppercase
                  "
                >
                  Snapshots & Recordings
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(snapshot, index) in this.snapshots"
                :class="[
                  'my-2 flex relative rounded-lg cursor-pointer justify-start min-w-14',
                  selectedSnapshot?.id === snapshot.id && selectedSnapshot.type === snapshot.type
                    ? 'bg-gray-150 ring-2 ring-vice-teal-600'
                    : 'hover:bg-gray-150 border',
                ]"
                :key="index"
                v-tippy="{
                  content: snapshot.type === 'video' ? 'Recording' : 'Snapshot',
                  theme: 'light-border',
                  placement: 'right',
                  interactive: true,
                }"
                @click="selectSnapshot(snapshot)"
              >
                <td class="p-2 text-xs font-medium text-vice-teal-900">
                  <span class="flex items-center gap-1">
                    <VideoSnapshotIcon
                      v-if="snapshot.type === 'video'"
                      class="h-10 w-12 text-vice-tile-900"
                    />
                    <ImageSnapshotIcon
                      v-if="snapshot.type === 'image'"
                      class="h-10 w-12 text-vice-tile-900"
                    />
                    <span>
                      <span class="font-semibold">
                        {{ snapshot.device_type.replace(/_/g, ' ') }}
                        <span
                          v-if="snapshot.type === 'image'"
                          class="font-medium text-vice-dark-grey text-xxs"
                        >
                          &nbsp;({{ snapshot.metadata.width }}x{{ snapshot.metadata.height }})
                        </span>
                        <span v-if="snapshot.type === 'video'" class="text-gray-900 font-normal">
                          &nbsp;({{ humanDuration(snapshot.duration) }})
                        </span>
                      </span>
                      <br />
                      <span class="text-sm text-gray-500">
                        Captured at:
                        {{ formatedDate(snapshot.completed_at) }}
                      </span>
                    </span>
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Preview and Annotations Column -->
        <div class="relative w-full col-span-12 sm:col-span-6">
          <!-- Preview Section -->
          <div class="bg-white shadow border rounded-lg mb-6">
            <div class="p-4 border-b bg-light-grey rounded-t-lg">
              <h3 class="text-lg leading-7 font-medium text-vice-teal-700">
                {{ selectedSnapshot.type === 'image' ? 'Snapshot' : 'Recording' }} Preview
                <span class="text-lg font-semibold text-vice-teal-900">
                  ({{ selectedSnapshot?.device_type.replace(/_/g, ' ') }})
                </span>
              </h3>
            </div>
            <div v-if="selectedSnapshot" class="flex m-3 relative h-auto">
              <!-- Video Player for Recording -->
              <div v-if="selectedSnapshot.type === 'video'" class="relative w-full">
                <video
                  ref="videoPlayer"
                  :key="selectedSnapshot.id"
                  :controls="previewLoaded"
                  controlsList="nodownload"
                  @mouseenter="isMouseOver = true"
                  @mouseleave="isMouseOver = false"
                  @loadeddata="handleVideoLoaded"
                  @play="isPlaying = true"
                  @pause="isPlaying = false"
                  @ended="isPlaying = false"
                  :class="[
                    isAudio(selectedSnapshot.device_type)
                      ? 'h-12 bg-player-grey rounded-full'
                      : 'h-auto bg-black rounded-lg',
                    { 'opacity-0': !previewLoaded },
                  ]"
                  class="
                    ring-1 ring-vice-teal-700
                    w-full
                    shadow-lg
                    relative
                    overflow-hidden
                    block
                    object-contain
                    zoom-111
                    z-20
                  "
                  :src="selectedSnapshot.file_url"
                >
                  Your browser does not support the video tag.
                </video>
                <ViceLoader
                  v-if="!previewLoaded"
                  class="inset-0 flex items-center justify-center z-20"
                  colorClass="text-vice-teal-700"
                  size="h-9 w-9"
                />
                <!-- Comment Markers on Progress Bar -->
                <Transition>
                  <div
                    v-if="
                      selectedSnapshot.type === 'video' &&
                      selectedSnapshot.duration > 0 &&
                      previewLoaded &&
                      showPins
                    "
                    :class="[
                      isAudio(selectedSnapshot.device_type)
                        ? 'absolute bottom-10 left-9 right-0 h-1 pointer-events-none mx-16'
                        : 'absolute bottom-9 left-0 right-0 h-1 pointer-events-none mx-5',
                      { 'opacity-0': !previewLoaded },
                    ]"
                  >
                    <div
                      v-for="comment in commentsWithTimestamps(selectedSnapshot.comments)"
                      :key="`marker-${comment.id}`"
                      :style="{ left: `${commentShift(comment.timestamp)}%` }"
                      @click="scrollToComment(comment.id)"
                      class="
                        absolute
                        top-6
                        transform
                        -translate-x-1/2
                        pointer-events-auto
                        cursor-pointer
                        group
                        z-20
                      "
                    >
                      <div
                        class="
                          w-6
                          h-6
                          border-2 border-white
                          shadow-lg
                          text-white text-xxs
                          font-semibold
                          flex
                          items-center
                          justify-center
                          transform
                          rotate-135
                          hover:scale-125
                          transition-transform
                          duration-200
                          raindrop
                        "
                        :class="
                          comment.commenter.role === 'clinician'
                            ? 'bg-vice-medium-blue'
                            : 'bg-vice-teal-500'
                        "
                      >
                        <span class="rotate-225 block">
                          {{ getInitials(comment.commenter.name) }}
                        </span>
                      </div>
                    </div>
                  </div>
                </Transition>
              </div>

              <!-- Image Viewer for Snapshot -->
              <div v-if="selectedSnapshot.type === 'image'" class="relative w-full">
                <img
                  :key="selectedSnapshot.id"
                  :src="selectedSnapshot.url"
                  :alt="`${selectedSnapshot.device_type} snapshot`"
                  @load="previewLoaded = true"
                  class="w-full h-auto ring-1 ring-vice-teal-700 rounded-lg shadow-lg"
                  :class="{ 'opacity-0': !previewLoaded }"
                />
                <ViceLoader
                  v-if="!previewLoaded"
                  class="inset-0 flex top-31 justify-center z-20"
                  colorClass="text-vice-teal-700"
                  size="h-9 w-9"
                />
                <ArrowsExpandIcon
                  class="h-7 w-7 right-1 top-1 absolute text-white cursor-pointer"
                  v-if="!snapshotPreviewOpen"
                  @click="snapshotPreviewOpen = true"
                />
                <TransitionRoot as="template" :show="snapshotPreviewOpen">
                  <Dialog
                    as="div"
                    class="relative zoom-80 z-30"
                    @close="snapshotPreviewOpen = false"
                  >
                    <TransitionChild
                      as="template"
                      enter="ease-out duration-300"
                      enter-from="opacity-0"
                      enter-to="opacity-100"
                      leave="ease-in duration-200"
                      leave-from="opacity-100"
                      leave-to="opacity-0"
                    >
                      <div class="fixed inset-0 bg-black bg-opacity-70 transition-opacity" />
                    </TransitionChild>

                    <div class="fixed inset-0 z-10 overflow-y-auto">
                      <div
                        class="
                          flex
                          min-h-full
                          items-center
                          md:items-end
                          justify-center
                          p-4
                          text-center
                          sm:items-center
                          sm:p-0
                        "
                      >
                        <TransitionChild
                          as="template"
                          enter="ease-out duration-300"
                          enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                          enter-to="opacity-100 translate-y-0 sm:scale-100"
                          leave="ease-in duration-200"
                          leave-from="opacity-100 translate-y-0 sm:scale-100"
                          leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        >
                          <DialogPanel
                            class="
                              relative
                              transform
                              overflow-hidden
                              rounded-lg
                              bg-white
                              px-4
                              pt-5
                              pb-4
                              text-left
                              shadow-xl
                              transition-all
                              max-h-screen
                              sm:my-8
                              sm:max-w-7xl
                              sm:p-6
                              min-w-20 min-h-20
                            "
                          >
                            <ViceLoader
                              colorClass="text-vice-teal-700"
                              class="left-2 z-10 bottom-0"
                            />
                            <img
                              class="w-full max-h-screen rounded relative z-20 block object-contain"
                              :src="selectedSnapshot.url"
                              alt=""
                            />
                          </DialogPanel>
                        </TransitionChild>
                      </div>
                    </div>
                  </Dialog>
                </TransitionRoot>
              </div>
            </div>
            <div v-else class="flex items-center justify-center h-full text-gray-500">
              Select a snapshot or recording to preview
            </div>

            <!-- Duration -->
            <div v-if="previewLoaded" class="flex items-center justify-between mx-3 mb-3">
              <div v-if="selectedSnapshot.duration_human" class="flex gap-2 text-sm text-gray-600">
                <span class="text-sm content-center text-gray-600 bg-gray-150 py-1 px-2 rounded-md">
                  Duration: {{ selectedSnapshot.duration_human }}
                </span>
              </div>
            </div>
          </div>

          <!-- Annotations Display (Read-only) -->
          <div class="shadow mt-6 border rounded-lg">
            <div class="p-4 border-b bg-light-grey rounded-t-lg">
              <h3 class="text-lg leading-7 font-medium text-vice-teal-700">Description</h3>
            </div>
            <div class="px-3 py-3 bg-white rounded-b-lg">
              <div
                v-if="selectedSnapshot.annotations"
                class="prose max-w-none text-sm"
                v-html="selectedSnapshot.annotations"
              ></div>
              <div v-else class="text-gray-500 text-sm italic">No description provided</div>
            </div>
          </div>
        </div>

        <!-- Comments Column -->
        <div class="col-span-12 sm:col-span-4">
          <div class="bg-white border shadow rounded-lg sticky top-6">
            <div class="p-4 border-b bg-light-grey rounded-t-lg">
              <h3 class="text-lg leading-7 font-medium text-vice-teal-700">Comments</h3>
            </div>
            <div class="bg-white rounded-b-lg" v-if="selectedSnapshot">
              <!-- Comments List -->
              <div class="px-3 py-3 space-y-4">
                <div
                  v-for="comment in selectedSnapshot.comments"
                  :key="comment.id"
                  :ref="`comment-${comment.id}`"
                  :class="[
                    'border rounded-md border-gray-200 p-3 transition-all duration-500',
                    highlightedCommentId === comment.id ? 'bg-vice-teal-50 ring-2' : '',
                  ]"
                >
                  <div class="flex justify-between pb-1">
                    <div class="flex items-center">
                      <div class="flex items-center">
                        <img
                          v-if="comment.commenter.profile_photo_url"
                          :src="comment.commenter.profile_photo_url"
                          :alt="comment.commenter.name"
                          class="h-8 w-8 rounded-full object-cover"
                        />
                        <div
                          v-else
                          class="
                            h-8
                            w-8
                            rounded-full
                            flex
                            items-center
                            justify-center
                            text-white
                            font-medium
                          "
                          :class="
                            comment.commenter.role === 'clinician'
                              ? 'bg-vice-medium-blue'
                              : 'bg-vice-teal-500'
                          "
                        >
                          {{ getInitials(comment.commenter.name) }}
                        </div>
                      </div>
                      <div class="flex ml-2 gap-2">
                        <p class="flex text-sm font-medium text-gray-900">
                          {{ comment.commenter.name }}
                        </p>
                        <span
                          v-if="comment.commenter.id === $page.props.session.details.clinician"
                          class="text-xxs content-center text-gray-600 bg-gray-150 px-1 rounded-md"
                        >
                          Author
                        </span>
                        <span
                          v-else-if="comment.commenter.role === 'clinician'"
                          class="
                            text-xxs
                            content-center
                            bg-vice-light-blue
                            text-gray-600
                            px-1
                            rounded-md
                          "
                        >
                          Clinician
                        </span>
                        <span
                          v-else
                          class="
                            text-xxs
                            content-center
                            bg-vice-teal-100
                            text-gray-600
                            px-1
                            rounded-md
                          "
                        >
                          Student
                        </span>
                      </div>
                    </div>

                    <div class="flex gap-2 items-center">
                      <span class="flex text-xxs text-gray-400">
                        {{ formatDate(comment.created_at) }}
                      </span>
                      <button
                        v-if="canDeleteComment(comment)"
                        @click="deleteComment(comment.id)"
                        class="
                          text-red-500
                          hover:text-red-700
                          transition-colors
                          duration-200
                          p-1
                          rounded
                          hover:bg-red-50
                        "
                        title="Delete comment"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          class="h-4 w-4"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                          />
                        </svg>
                      </button>
                    </div>
                  </div>
                  <div class="flex items-start space-x-3">
                    <div class="flex-1 mt-1 min-w-0">
                      <span
                        v-if="comment.timestamp !== null && comment.timestamp !== undefined"
                        @click="seekToTimestamp(comment.timestamp)"
                        class="
                          px-2
                          py-1
                          bg-vice-teal-600
                          hover:bg-vice-teal-700
                          rounded-full
                          text-xs text-vice-teal-100
                          cursor-pointer
                          font-bold
                          hover:bg-vice-teal-150
                          justify-evenly
                          w-20
                          flex
                          leading-5
                        "
                      >
                        <span class="px-1 text-xs font-semibold text-gray-100">▶</span>
                        {{ formatTimestamp(comment.timestamp) }}
                      </span>
                      <p
                        class="
                          mt-1
                          ml-1
                          text-sm text-gray-700
                          hover:text-gray-900
                          whitespace-pre-wrap
                        "
                      >
                        {{ comment.comment }}
                      </p>
                    </div>
                  </div>
                </div>
                <div
                  v-if="selectedSnapshot.comments.length === 0"
                  class="text-gray-500 text-sm text-center py-2"
                >
                  No comments yet.
                </div>
              </div>

              <!-- Add Comment Form -->
              <div class="border-t border-gray-200 p-3">
                <form @submit.prevent="submitComment">
                  <textarea
                    v-model="newComment"
                    rows="3"
                    class="
                      block
                      w-full
                      rounded-md
                      border-gray-300
                      shadow-sm
                      focus:border-vice-teal-500
                      focus:ring-vice-teal-500
                      text-sm
                    "
                    placeholder="Add a comment..."
                    :disabled="isSubmitting"
                  ></textarea>
                  <div class="mt-3 flex justify-end">
                    <button
                      type="submit"
                      :disabled="!newComment.trim() || isSubmitting"
                      class="
                        inline-flex
                        items-center
                        px-4
                        py-2
                        border border-transparent
                        rounded-md
                        shadow-sm
                        text-sm
                        font-medium
                        text-white
                        bg-vice-teal-500
                        hover:bg-vice-teal-600
                        focus:outline-none
                        focus:ring-2 focus:ring-offset-2 focus:ring-vice-teal-500
                        disabled:opacity-50
                        disabled:cursor-not-allowed
                      "
                    >
                      <ViceLoader v-if="isSubmitting" class="mr-2 h-4 w-4" />
                      <span>{{ isSubmitting ? 'Posting...' : 'Post Comment' }}</span>
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- No snapshots or recordings available -->
      <div
        v-else
        class="text-gray-500 text-center justify-center"
        :class="tabs[2].current ? 'block' : 'hidden'"
      >
        No recordings or snapshots have been shared with you yet.
      </div>

      <!-- Reflection tab -->
      <show-detail-reflection
        ref="reflectionTab"
        :class="tabs[3].current ? 'block' : 'hidden'"
      ></show-detail-reflection>
    </div>
  </app-layout>
</template>

<script>
import dayjs from 'dayjs';
import { directive } from 'vue-tippy';
import { ClockIcon, ChevronLeftIcon, AcademicCapIcon } from '@heroicons/vue/solid';
import { CalendarIcon, ArrowsExpandIcon, LocationMarkerIcon } from '@heroicons/vue/outline';
import { Dialog, DialogPanel, TransitionChild, TransitionRoot } from '@headlessui/vue';
import { ref } from 'vue';
import { isMobileDevice } from '@/Utils/helpers';
import DateFormatMixin from '@/Mixins/DateFormatMixin.vue';

import AppLayout from '@/Layouts/AppLayout.vue';

import SessionEntryCard from './Details/SessionEntryCard.vue';

import ProofOfAttendance from './Details/ProofOfAttendance.vue';
import AboutSession from './Details/AboutSession.vue';
import SessionFiles from './Details/SessionFiles.vue';
import Application from './Details/Application.vue';
import Invitation from './Details/Invitation.vue';
import AboutClinician from './Details/AboutClinician.vue';
import WhatYouLearn from './Details/WhatYouLearn.vue';
import ShowDetailReflection from './ShowDetailReflection.vue';
import SessionEntryWizard from './Details/SessionEntryWizard.vue';
import CourseRequiredAlert from '@/Components/Alerts/CourseRequiredAlert';
import ViceButton from '@/Components/Buttons/ViceButton.vue';
import SessionEntryPrecheckWizard from '@/Pages/User/Details/SessionEntryPrecheckWizard.vue';
import ViceLoader from '@/Components/Loaders/ViceLoader.vue';
import VideoSnapshotIcon from '@/Components/Icons/VideoSnapshotIcon.vue';
import ImageSnapshotIcon from '@/Components/Icons/ImageSnapshotIcon.vue';
import 'tippy.js/dist/tippy.css';
import 'tippy.js/themes/light-border.css';
import ActionMessage from '@/Jetstream/ActionMessage.vue';

export default {
  name: 'ShowDetail',
  directives: {
    tippy: directive,
  },
  mounted() {
    this.setSessionUserState();
    this.determineDefaultTab();
    this.sortSnapshots();
  },
  setup() {
    const hasSession = ref(false);
    const hasApplied = ref(false);
    const applicationId = ref(false);
    const hasInvite = ref(false);
    const hasPendingInvite = ref(false);
    const userSession = ref(null);
    const userSessionType = ref('');
    const highlightedCommentId = ref(null);
    const newComment = ref('');
    const isSubmitting = ref(false);
    const isPlaying = ref(false);
    const isMouseOver = ref(false);
    const videoPlayer = ref(null);

    return {
      highlightedCommentId,
      newComment,
      isSubmitting,
      isPlaying,
      isMouseOver,
      videoPlayer,
      hasSession,
      hasApplied,
      applicationId,
      hasInvite,
      hasPendingInvite,
      userSession,
      userSessionType,
    };
  },
  mixins: [DateFormatMixin],
  components: {
    ActionMessage,
    SessionEntryPrecheckWizard,
    ShowDetailReflection,
    WhatYouLearn,
    AboutClinician,
    Application,
    Invitation,
    ProofOfAttendance,
    AboutSession,
    SessionEntryCard,
    SessionEntryWizard,
    AppLayout,
    ChevronLeftIcon,
    CalendarIcon,
    ClockIcon,
    AcademicCapIcon,
    LocationMarkerIcon,
    SessionFiles,
    CourseRequiredAlert,
    ViceButton,
    ViceLoader,
    Dialog,
    DialogPanel,
    TransitionRoot,
    TransitionChild,
    ArrowsExpandIcon,
    VideoSnapshotIcon,
    ImageSnapshotIcon,
  },
  data() {
    return {
      user_invites: this.$page.props.session.users,
      snapshots: [],
      tabs: [
        {
          id: 'overview',
          name: 'Overview',
          current: true,
          visible: true,
        },
        { id: 'more_information', name: 'More Information', current: false, visible: false },
        {
          id: 'recordings',
          name: 'Recordings',
          current: false,
          visible: this.$page.props.isSnapshotsAndRecordingsEnabled,
        },
        {
          id: 'reflection',
          name: 'Reflection',
          current: false,
          visible: this.$page.props.inviteAccepted,
        },
      ],
      selectedSnapshot: null,
      previewLoaded: false,
      snapshotPreviewOpen: false,

      isOpenSessionEntryWizard: false,
      isOpenSessionEntryPrecheck: false,

      isMobile: isMobileDevice(),
      isObserver: this.$page.props.participation_role === 'observer',
    };
  },
  computed: {
    showPins() {
      return !this.isPlaying || this.isMouseOver;
    },
    snapshotsExist() {
      return this.snapshots.length > 0;
    },
  },
  methods: {
    commentsWithTimestamps(comments) {
      return comments.filter(
        (comment) => comment.timestamp !== null && comment.timestamp !== undefined,
      );
    },

    formatDate(date) {
      return dayjs(date).fromNow();
    },

    formatTimestamp(seconds) {
      const minutes = Math.floor(seconds / 60);
      const secs = Math.floor(seconds % 60);
      return `${minutes}:${secs.toString().padStart(2, '0')}`;
    },

    handleVideoLoaded() {
      this.previewLoaded = true;
      if (this.videoPlayer) {
        this.videoDuration = this.videoPlayer.duration;
      }
    },

    seekToTimestamp(timestamp) {
      if (this.videoPlayer && timestamp !== null && timestamp !== undefined) {
        this.videoPlayer.currentTime = parseFloat(timestamp);
        window.scrollTo({ top: 0, behavior: 'smooth' });
        this.videoPlayer.play();
      }
    },

    commentShift(timestamp) {
      return Math.round((timestamp / (this.selectedSnapshot.duration / 1000)) * 100);
    },

    scrollToComment(commentId) {
      const commentElement = this.$refs[`comment-${commentId}`];
      if (commentElement) {
        const element = Array.isArray(commentElement) ? commentElement[0] : commentElement;
        // Scroll to the comment
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        // Highlight the comment
        this.highlightedCommentId = commentId;

        setTimeout(() => {
          this.highlightedCommentId = null;
        }, 2000);
      }
    },

    getInitials(name) {
      return name
        .split(' ')
        .map((n) => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2);
    },

    async submitComment() {
      if (!this.newComment.trim()) return;

      this.added = false;
      this.isSubmitting = true;

      // Capture current video playback time
      const currentTime = this.videoPlayer?.currentTime || null;
      try {
        const commentData = {
          comment: this.newComment,
          timestamp: currentTime,
        };

        const response = await axios.post(
          route('user.shared-content.comments.store', {
            type: this.selectedSnapshot.type === 'image' ? 'snapshot' : 'recording',
            id: this.selectedSnapshot.id,
          }),
          commentData,
        );

        if (response.data.success) {
          this.selectedSnapshot.comments.push(response.data.data);
          this.newComment = '';
          this.added = true;
        } else {
          console.error('Failed to add comment:', response.data.message);
        }
      } catch (error) {
        console.error('Error adding comment:', error);
      } finally {
        this.added = false;
        this.isSubmitting = false;
      }
    },

    canDeleteComment(comment) {
      return comment.commenter_id === this.$page.props.user.id;
    },

    async deleteComment(commentId) {
      try {
        const response = await axios.delete(
          route('user.shared-content.comments.destroy', {
            commentId,
          }),
        );

        if (response.data.success) {
          const index = this.selectedSnapshot.comments.findIndex((c) => c.id === commentId);
          if (index !== -1) {
            this.selectedSnapshot.comments.splice(index, 1);
          }
        } else {
          console.error('Failed to delete comment:', response.data.message);
        }
      } catch (error) {
        console.error('Error deleting comment:', error);
      }
    },

    setSessionUserState() {
      this.hasSession = this.$page.props.hasSession;
      this.hasApplied = this.$page.props.hasApplied;
      this.applicationId = this.$page.props.applicationId;
      this.hasInvite = this.$page.props.hasInvite;
      this.hasPendingInvite = this.$page.props.hasPendingInvite;
      this.userSessionType = this.$page.props.userType;
      if (this.$page.props.reflection) {
        this.setTabState(this.tabs[3].id);
      }
    },

    appliedUser(userType) {
      this.hasApplied = true;
      this.userSessionType = userType;
    },
    checkIfHasContent(content) {
      if (content) {
        return Boolean(content.replace(/<\/?[^>]+(>|$)/g, ''));
      }
      return false;
    },
    setTabState(id) {
      const leavingReflection = this.tabs.find((tab) => tab.id === 'reflection' && tab.current);
      if (
        leavingReflection &&
        id !== 'reflection' &&
        !this.$refs.reflectionTab?.confirmDiscardChanges()
      ) {
        return;
      }
      this.tabs.forEach((tab) => {
        tab.current = tab.id === id;
      });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    openSessionEntryWizard() {
      localStorage.setItem('_vice:userSessionDetailsTour_current_step', 1);
      this.isOpenSessionEntryWizard = true;
    },
    openSessionEntryPrecheck() {
      this.isOpenSessionEntryPrecheck = true;
    },
    participateOffline() {
      window.location.href = route(
        'session.participate_offline',
        this.$page.props.session.unique_id,
      );
    },
    determineDefaultTab() {
      const urlParams = new URLSearchParams(window.location.search);
      const queryParam = urlParams.get('tab');
      const availableTabIds = ['overview', 'reflection', 'recordings'];

      if (!queryParam || !availableTabIds.includes(queryParam)) {
        this.setTabState('overview');
      } else {
        this.setTabState(queryParam);
      }
    },
    sortSnapshots() {
      const snapshots = this.$page.props.snapshotsAndRecordings.snapshots.map((s) => ({
        ...s,
        type: 'image',
      }));

      const recordings = this.$page.props.snapshotsAndRecordings.recordings.map((r) => ({
        ...r,
        type: 'video',
      }));

      this.snapshots = [...snapshots, ...recordings].sort(
        (a, b) => new Date(a.completed_at) - new Date(b.completed_at),
      );

      if (this.snapshotsExist) {
        this.selectedSnapshot = this.snapshots[0];
      }
    },
    selectSnapshot(snapshot) {
      if (snapshot === this.selectedSnapshot) return;

      this.previewLoaded = false;
      this.selectedSnapshot = snapshot;
    },
    formatedDate(date) {
      return dayjs(date).format('HH:mm:ss') ?? '';
    },
    humanDuration(duration) {
      const totalSeconds = Math.floor(duration / 1000);
      const minutes = Math.floor(totalSeconds / 60);
      const seconds = totalSeconds % 60;

      return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    },
    isAudio(type) {
      return type === 'Stethoscope';
    },
  },
};
</script>

<style scoped>
.prose {
  color: #374151;
}

.prose :deep(p) {
  margin-bottom: 0.5rem;
}

.prose :deep(ul),
.prose :deep(ol) {
  margin-left: 1.5rem;
  margin-bottom: 0.5rem;
}

.prose :deep(li) {
  margin-bottom: 0.25rem;
}

.raindrop {
  border-radius: 50% 50% 50% 0;
}

.bg-player-grey {
  background-color: #1f2937;
}

.v-enter-active {
  transition: opacity 0.5s ease;
}

.v-leave-active {
  transition: opacity 1s ease;
}

.v-enter-from,
.v-leave-to {
  opacity: 0;
}
</style>
